/**
 * Which root shell a signed-in URL gets (layered shell surfaces, D22, R-17, R-33, R-34, WS-6
 * §3.5).
 *
 * The public `GET /v1/root-shell` answer is untrusted: it arrives before any credential exists.
 * The loader takes an app slug and a surface name from it and nothing else. Every bundle URL comes
 * from the principal's authed `GET /v1/apps`.
 */
import type { SurfaceTarget } from '@zion/sdk-surfaces/v1';
import { ROOT_SHELL_APPS } from '@zion/sdk-contracts';
import {
  GET_ROOT_SHELL_ROUTE,
  getRootShellQuerySchema,
  getRootShellResponseSchema,
  type GetRootShellResponse,
} from '@zion/sdk-dp-admin-contracts';
import type { AppRegistryEntry } from '../api/types.js';

/** The platform's default root shell (D24, D36). Registered before stage 1 merged (R-14). */
export const DEFAULT_ROOT_APP_ID = 'default-shell';

/** How long the public root-shell request may take before the loader takes the default. */
export const ROOT_SHELL_FETCH_TIMEOUT_MS = 10_000;

/** The public route's 200 body. */
export type RootShellResponse = GetRootShellResponse;

/** The assignment the loader resolves: from the org's rows, or the platform default. */
export interface RootAssignment {
  readonly appId: string;
  /** The assigned root surface, or `null` for the default: the row's one root surface. */
  readonly surface: string | null;
  readonly source: 'assignment' | 'default';
}

/** Why an assignment cannot mount. */
export type RootUnavailable = 'app-not-in-registry' | 'surface-not-declared' | 'not-a-root-surface' | 'ambiguous-root';

/** What the loader mounts at a root path. */
export type RootSelection =
  | {
      readonly kind: 'surface';
      readonly target: SurfaceTarget;
      readonly entry: AppRegistryEntry;
      /** The row's root declaration the target names; the loader reads its `uiPackage` (R-102). */
      readonly declaration: AppRegistryEntry['surfaces'][number];
    }
  | { readonly kind: 'interim' }
  | { readonly kind: 'unavailable'; readonly reason: RootUnavailable };

const DEFAULT_ASSIGNMENT: RootAssignment = Object.freeze({ appId: DEFAULT_ROOT_APP_ID, surface: null, source: 'default' });

/**
 * Read the org's assignment for `pathname`.
 *
 * Sends one unauthenticated `GET /v1/root-shell?path=<pathname>` with a
 * {@link ROOT_SHELL_FETCH_TIMEOUT_MS} timeout; a `pathname` outside the route's query grammar is
 * left off, so core-api reads `/`. A 200 whose body passes `getRootShellResponseSchema` and whose
 * `appId` is on `ROOT_SHELL_APPS` answers `{ appId, surface, source: 'assignment' }`. Every other
 * answer takes the default `{ appId: 'default-shell', surface: null, source: 'default' }`: a 204
 * with no body (the host maps to an org that has no matching row, D-2), a 404 (`no org for this
 * host`, or `no root shell assigned` from a core-api older than D-2), any other status, a network
 * error, a timeout, a body that is not JSON or that the schema refuses, and an app off the list.
 * The function reads no body for a status other than 200 and writes nothing to the console. Never
 * rejects.
 */
export async function resolveRootAssignment(fetcher: typeof fetch, pathname: string): Promise<RootAssignment> {
  const query = getRootShellQuerySchema.safeParse({ path: pathname });
  const url = query.success ? `${GET_ROOT_SHELL_ROUTE.path}?path=${encodeURIComponent(pathname)}` : GET_ROOT_SHELL_ROUTE.path;
  try {
    const res = await fetcher(url, {
      credentials: 'same-origin',
      signal: AbortSignal.timeout(ROOT_SHELL_FETCH_TIMEOUT_MS),
    });
    if (res.status !== 200) return DEFAULT_ASSIGNMENT;
    const parsed = getRootShellResponseSchema.safeParse(JSON.parse(await res.text()));
    if (!parsed.success || !ROOT_SHELL_APPS.includes(parsed.data.appId)) return DEFAULT_ASSIGNMENT;
    return Object.freeze({ appId: parsed.data.appId, surface: parsed.data.surface, source: 'assignment' });
  } catch {
    return DEFAULT_ASSIGNMENT;
  }
}

/**
 * Pick the root surface for an assignment from the principal's registry. Pure.
 *
 * - No row with the assignment's `appId`: `interim` when `interimAvailable`, else
 *   `app-not-in-registry`.
 * - A named `surface` the row does not declare: `surface-not-declared`; one that is not a routed
 *   `root: true` declaration: `not-a-root-surface`.
 * - `surface: null`: the row's one routed `root: true` declaration; none gives
 *   `not-a-root-surface`, two or more `ambiguous-root`.
 * - Success: `{ kind: 'surface', target: { app: row.appId, surface: name }, entry: row,
 *   declaration }`, where `declaration` is the row's root declaration of that name.
 */
export function selectRootSurface(
  registry: readonly AppRegistryEntry[],
  assignment: RootAssignment,
  interimAvailable: boolean,
): RootSelection {
  const entry = registry.find((row) => row.appId === assignment.appId);
  if (entry === undefined) {
    return interimAvailable ? { kind: 'interim' } : { kind: 'unavailable', reason: 'app-not-in-registry' };
  }
  const isRoot = (decl: AppRegistryEntry['surfaces'][number]): boolean => decl.kind === 'routed' && decl.root === true;
  if (assignment.surface !== null) {
    const decl = entry.surfaces.find((d) => d.name === assignment.surface);
    if (decl === undefined) return { kind: 'unavailable', reason: 'surface-not-declared' };
    if (!isRoot(decl)) return { kind: 'unavailable', reason: 'not-a-root-surface' };
    return { kind: 'surface', target: { app: entry.appId, surface: decl.name }, entry, declaration: decl };
  }
  const roots = entry.surfaces.filter(isRoot);
  if (roots.length === 0) return { kind: 'unavailable', reason: 'not-a-root-surface' };
  if (roots.length > 1) return { kind: 'unavailable', reason: 'ambiguous-root' };
  const root = roots[0] as AppRegistryEntry['surfaces'][number];
  return { kind: 'surface', target: { app: entry.appId, surface: root.name }, entry, declaration: root };
}
